/**
 * User Context
 *
 * This module defines the React Context for persisting User information and permissions management.
 * It provides a centralized way to access this User information for any Consumer.
 */

import { useAuth } from '@utils/auth/AuthProvider';
import { cloneDeep, includes, some } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useState } from 'react';
import { PermissionLevel, PermissionType, UserContextType } from 'types/UserContext.types';

export { PermissionLevel, PermissionType };
export type { UserContextType };

/**
 * Initial User State
 *
 * Default values for the user context when no user is authenticated.
 */
const initialUser: UserContextType = {
  username: '',
  attributes: {},
  permissionLevels: {
    [PermissionLevel.DEV_TEAM_ADMIN]: false,
    [PermissionLevel.AURIGA_SUPER_USER]: false,
    [PermissionLevel.AURIGA_BUSINESS_USER]: false,
    [PermissionLevel.AURIGA_ADMIN]: false,
  },
  permissions: {
    [PermissionType.READ]: true, // Default to TRUE, unless otherwise informed by API that READ is not granted
    [PermissionType.WRITE]: false,
    [PermissionType.NO_DATA]: false,
    [PermissionType.ADD_ACCESS]: false,
    [PermissionType.REMOVE_ACCESS]: false,
  },
  setUser: () => {},
  hasPermissionLevel: () => false,
  hasPermission: () => false,
};

/**
 * User Context
 *
 * React context for User information and application Permission gating.
 */
export const UserContext = createContext<UserContextType>(initialUser);

/**
 * User Provider Props Interface
 *
 * Defines the props for the UserProvider component.
 */
interface UserProviderProps {
  children: ReactNode;
}

/**
 * User Provider Component
 *
 * This component provides user context to the entire application.
 * It handles user information, attributes, and permissions level management.
 *
 * Key responsibilities:
 * - Extract user information from authentication context
 * - Determine user roles and permissions based on LDAP groups
 * - Provide methods to check permissions and roles
 * - Make user information available throughout the component tree
 *
 * @param {UserProviderProps} props - The component props
 * @returns {JSX.Element} The provider component
 */
export const UserProvider = ({ children }: UserProviderProps): ReactNode => {
  const { user: authUser, isAuthenticated } = useAuth();
  const [user, setUserState] = useState<Omit<UserContextType, 'setUser' | 'hasPermissionLevel' | 'hasPermission'>>({
    username: initialUser.username,
    attributes: initialUser.attributes,
    permissionLevels: initialUser.permissionLevels,
    permissions: initialUser.permissions,
  });

  /**
   * Update user information
   *
   * @param {Partial<UserContextType>} newUserData - The new user data to merge with existing data
   */
  const updateUser = useCallback((newUserData: Partial<UserContextType>) => {
    setUserState((prevUser) => ({ ...prevUser, ...newUserData }));
  }, []);

  /**
   * Check if user has a specific permission level
   *
   * @param {string} permission - The permission level to check
   * @returns {boolean} True if the user has the permission level, false otherwise
   */
  const hasPermissionLevel = useCallback(
    (permission: PermissionLevel): boolean => {
      return user.permissionLevels[permission];
    },
    [user.permissionLevels],
  );

  /**
   * Check if user has a specific permission
   *
   * @param {string} permission - The permission type
   * @returns {boolean} True if the user has the permission, false otherwise
   */
  const hasPermission = useCallback(
    (permission: PermissionType): boolean => {
      return user.permissions[permission];
    },
    [user.permissions],
  );

  /**
   * Determine user permissions based on Permission Level
   *
   * @returns {Record<string, boolean>} The user permissions
   */
  const determinePermissions = useCallback((): Record<PermissionType, boolean> => {
    const permissions = cloneDeep(initialUser.permissions); // Start with defined defaults

    // If a User has any defined permission level set to TRUE, continue to grant read access
    permissions[PermissionType.READ] = some(user.permissionLevels, (level) => level);

    // Super User specific permission adjustments based on BRD
    if (hasPermissionLevel(PermissionLevel.AURIGA_SUPER_USER)) {
      permissions[PermissionType.WRITE] = true;
    }

    // Business User permissions
    if (hasPermissionLevel(PermissionLevel.AURIGA_BUSINESS_USER)) {
      permissions[PermissionType.NO_DATA] = true;
    }

    // Admin User permissions
    if (hasPermissionLevel(PermissionLevel.AURIGA_ADMIN)) {
      permissions[PermissionType.ADD_ACCESS] = true;
      permissions[PermissionType.REMOVE_ACCESS] = true;
    }

    return permissions;
  }, [hasPermissionLevel, user.permissionLevels]);

  /**
   * Extract user information from authentication context
   *
   * This effect runs when the authentication state changes and:
   * - Extracts user information from the authentication context
   * - Determines user permissions based on LDAP groups
   * - Updates the user state
   */
  useEffect(() => {
    if (isAuthenticated && authUser) {
      const attributes = authUser.attributes || {};
      const permissions = determinePermissions();
      const permissionLevels = cloneDeep(initialUser.permissionLevels);

      // Dev Team Admin permissions
      const isDevTeamAdmin =
        includes(attributes?.['custom:LDAP'], 'gft-pie-team') || includes(attributes?.['custom:LDAP'], 'gft-capella');
      if (isDevTeamAdmin) {
        permissionLevels[PermissionLevel.DEV_TEAM_ADMIN] = true;
      }

      // TODO: Revisit determinePermissions() logic and frontend permission levels for P1 of Auriga
      updateUser({
        username: authUser.attributes['custom:USERNAME'] || 'auriga-user',
        attributes,
        permissions,
        permissionLevels,
      });
    } else {
      // Reset user state when not authenticated
      updateUser({
        username: '',
        attributes: {},
        permissions: {
          [PermissionType.READ]: false,
          [PermissionType.WRITE]: false,
          [PermissionType.NO_DATA]: false,
          [PermissionType.ADD_ACCESS]: false,
          [PermissionType.REMOVE_ACCESS]: false,
        },
        permissionLevels: initialUser.permissionLevels,
      });
    }
    // determinePermissions intentionally omitted to prevent infinite loop. TODO refactor in Q1
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [isAuthenticated, authUser, updateUser]);

  // Combine state and methods for the context value
  const contextValue = useMemo<UserContextType>(
    () => ({
      ...user,
      setUser: updateUser,
      hasPermissionLevel,
      hasPermission,
    }),
    [hasPermission, hasPermissionLevel, updateUser, user],
  );

  return <UserContext value={contextValue}>{children}</UserContext>;
};

/**
 * Custom hook to access the user context
 *
 * This hook provides a convenient way to access user information and permissions
 * from any component in the application.
 *
 * @returns {UserContextType} The user context containing username, attributes, permissions, etc.
 */
export const useUserContext = (): UserContextType => use(UserContext);
